import { useState } from "react";
import type { ExpenseCategoryDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";

export function ExpenseDialog({
  open,
  onOpenChange,
  categories,
  onRecorded,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  categories: ExpenseCategoryDto[];
  onRecorded(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.recordExpense({ categoryId, amount, description, method, reference });
      push(t("finance.expenseRecordedToast"));
      setAmount("");
      setDescription("");
      setReference("");
      onOpenChange(false);
      onRecorded();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("finance.recordExpenseTitle")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={busy || !categoryId || !amount}
          >
            {t("finance.recordExpense")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <SelectInput
          label={t("common.fields.category")}
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
        />
        <TextInput
          label={t("common.fields.amount")}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <TextInput
          label={t("common.fields.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label={t("common.fields.method")}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          />
          <TextInput
            label={t("common.fields.reference")}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
